import { writable } from "svelte/store";

/**
 * A class to compare local and remote state.  You probably want to
 * subscribe to the `dirty` store to know if the local value differs
 * from the remote. This is useful to enable/disable save buttons.
 */
export class StagingStore {
  #localValue = null;
  #remoteValue = null;
  #subscribers = new Set();
  #unsubscribe = null;
  #clone = (value) => JSON.parse(JSON.stringify(value));
  #eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  #dirty = writable(false);

  constructor({ store, initialValue = null, clone, eq }) {
    this.#localValue = initialValue;
    this.#remoteValue = initialValue;
    if (clone) {
      this.#clone = clone;
    }
    if (eq) {
      this.#eq = eq;
    }
    this.#unsubscribe = store.subscribe((remoteValue) => {
      this.#remoteValue = this.#clone(remoteValue);
      this.#localValue = this.#clone(remoteValue);
      this.#notify();
    });
  }

  // Read-only
  get dirty() {
    return { subscribe: this.#dirty.subscribe };
  }

  #check() {
    if (!this.#unsubscribe) {
      throw new Error(
        "StagingStore has already been destroyed. Create a new instance to use it again.",
      );
    }
  }

  // Unsubscribe so can be GC'd
  destroy() {
    if (this.#unsubscribe) {
      this.#unsubscribe();
      this.#unsubscribe = null;
    }
  }

  // Reset to remote state.
  reset() {
    this.#check();
    this.#localValue = this.#clone(this.#remoteValue);
    this.#notify();
  }

  #notify() {
    this.#subscribers.forEach((subscriber) => subscriber(this.#localValue));
    this.#dirty.set(!this.#eq(this.#remoteValue, this.#localValue));
  }

  set(value) {
    this.#check();
    this.#localValue = this.#clone(value);
    this.#notify();
  }

  update(callback) {
    this.#check();
    this.#localValue = this.#clone(callback(this.#localValue));
    this.#notify();
  }

  subscribe(subscriber) {
    this.#subscribers.add(subscriber);
    subscriber(this.#localValue);
    return () => {
      this.#subscribers.delete(subscriber);
    };
  }
}
